import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCreateCommunity } from '@/features/communities/use-communities'

const schema = z.object({
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(60, 'Slug must be 1–60 characters')
    .regex(
      /^[a-z0-9]([a-z0-9-]{0,58}[a-z0-9])?$/,
      'Lowercase letters, numbers, or hyphens — no leading or trailing hyphen'
    ),
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(5000).optional(),
  accentColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Hex like #4F46E5')
    .optional()
    .or(z.literal('')),
})

type FormData = z.infer<typeof schema>

export const CommunityNewPage = () => {
  const navigate = useNavigate()
  const create = useCreateCommunity()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { slug: '', name: '', description: '', accentColor: '' },
  })

  const onSubmit = async (data: FormData) => {
    try {
      const created = await create.mutateAsync({
        slug: data.slug,
        name: data.name,
        description: data.description || undefined,
        accentColor: data.accentColor || undefined,
      })
      toast.success(`Created /c/${created.slug}`)
      navigate(`/c/${created.slug}`)
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status
      if (status === 409) toast.error('Slug already in use — try another')
      else if (status === 400) toast.error('Slug invalid or reserved — try another')
      else toast.error('Failed to create community')
    }
  }

  const pending = isSubmitting || create.isPending

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-6">
      <Button variant="ghost" size="sm" asChild className="gap-2 px-2">
        <Link to="/c">
          <ArrowLeft className="h-4 w-4" /> Back to communities
        </Link>
      </Button>
      <Card>
        <CardHeader>
          <CardTitle>New community</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground">/c/</span>
                <Input id="slug" placeholder="recipes" {...register('slug')} />
              </div>
              {errors.slug && <p className="text-xs text-destructive">{errors.slug.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" placeholder="Recipes" {...register('name')} />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                rows={4}
                placeholder="Everyday cooking, from prep to plate."
                {...register('description')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="accentColor">Accent color (optional)</Label>
              <Input id="accentColor" placeholder="#4F46E5" {...register('accentColor')} />
              {errors.accentColor && (
                <p className="text-xs text-destructive">{errors.accentColor.message}</p>
              )}
            </div>
            <Button type="submit" disabled={pending}>
              {pending ? 'Creating…' : 'Create community'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
